import store from "state/store.js";
import { round } from "utils/round-functions/round";
import { findCurrentFee } from "utils/fee-functions/calculate-comission";
import { AvailableCurrency } from "@pano-project/utils/getLimitsForCurrency";
import { getWalletCurrencyLimits } from "utils/hooks/digital-wallets/helpers";

export const getBaseAmountFromTotalCharge = ({ 
    chargeCurrency, 
    chargeAmount = 0,
    predefinedFees,
    predefinedCurrencyInfo
}: { 
    chargeCurrency: string,
    chargeAmount: number,
    predefinedFees?: Record<string, any>,
    predefinedCurrencyInfo?: Record<string, any>,
}) => {
    const {
        profileInfo: { available_currencies: { currencies = [] } = {} },
        currentGateway,
        currentGateway: { payment_information: { currency_list = {} } = {} } = {},
        profileInfo: { base_currency = '', transaction_type = '' } = {}
    } = store.getState();

    const fees = predefinedFees || currentGateway?.fees || {};

    const currencyInfo = predefinedCurrencyInfo
        || getWalletCurrencyLimits(chargeCurrency)
        || currency_list?.[chargeCurrency] 
        || currencies?.find((currency: AvailableCurrency) => currency.code === chargeCurrency);
    if (!currencyInfo) return null;

    const { rate } = currencyInfo;
    if (!chargeAmount || !rate) return null;

    const chargeAmountNumber = Number(chargeAmount);

    const chargeAmountFee = findCurrentFee(fees?.[chargeCurrency] || [], chargeAmountNumber, chargeCurrency);
    if (!chargeAmountFee) return round(chargeAmountNumber / rate, base_currency);

    const {min_fee_amount, max_fee_amount, flat_fee_amount, percentage_fee} = chargeAmountFee;
    const maxFee = max_fee_amount ?? Infinity;
    const minFee = min_fee_amount ?? 0;
    const flatFee = flat_fee_amount ?? 0;
    const variable = (percentage_fee ?? 0) / 100;

    let amountWithFeeAdded;
    let amountWithFeeIncluded;

    if (transaction_type === 'pay-out') {
        amountWithFeeAdded = 
            (chargeAmountNumber + Math.min(maxFee, Math.max(minFee, (flatFee + variable * chargeAmountNumber)))) / rate;
        amountWithFeeIncluded = 
            (chargeAmountNumber + Math.min(maxFee, Math.max(minFee, (flatFee + variable * ((chargeAmountNumber + flatFee) / (1 - variable)))))) / rate;
    }

    if (transaction_type === 'pay-in') {
        amountWithFeeAdded = 
            (chargeAmountNumber - Math.min(maxFee, Math.max(minFee, (flatFee + variable * ((chargeAmountNumber - flatFee) / (1 + variable)))))) / rate;
        amountWithFeeIncluded = 
            (chargeAmountNumber - Math.min(maxFee, Math.max(minFee, (flatFee + variable * chargeAmountNumber)))) / rate;
    }

    return chargeAmountFee.is_included 
        ? round(amountWithFeeIncluded, base_currency) 
        : round(amountWithFeeAdded, base_currency);
}

export const getTotalChargeFromBaseCurrencyAmount = ({ 
    chargeCurrency,
    gateway,
    amount = 0,
    predefinedFees,
    predefinedCurrencyInfo
}: { 
    chargeCurrency: string,
    gateway: Record<string, any> | undefined,
    amount?: number,
    predefinedFees?: Record<string, any>,
    predefinedCurrencyInfo?: Record<string, any>,
 }) => {
    const {
        profileInfo: { available_currencies: { currencies = [] } = {} },
        profileInfo: { transaction_type = '' } = {}
    } = store.getState();

    const fees = predefinedFees || gateway?.fees || {};
    const currency_list = gateway?.payment_information?.currency_list || {}

    const currencyInfo = predefinedCurrencyInfo 
        || getWalletCurrencyLimits(chargeCurrency)
        || currency_list?.[chargeCurrency] 
        || currencies?.find((currency: AvailableCurrency) => currency.code === chargeCurrency);

    if (!currencyInfo) return null;

    const { rate } = currencyInfo;
    if (!amount || !rate) return null;

    const total = Number(amount * rate);

    const amountFee = findCurrentFee(fees?.[chargeCurrency] || [], total, chargeCurrency);
    if (!amountFee) return round(total, chargeCurrency);

    const {min_fee_amount, max_fee_amount, flat_fee_amount, percentage_fee} = amountFee;
    const maxFee = max_fee_amount ?? Infinity;
    const minFee = min_fee_amount ?? 0;
    const flatFee = flat_fee_amount ?? 0;
    const variable = (percentage_fee ?? 0) / 100;

    let chargeAmountWithFeeAdded;
    let chargeAmountWithFeeIncluded;

    if (transaction_type === 'pay-out') {
        chargeAmountWithFeeAdded =
            Math.min(total - minFee, Math.max(total - maxFee, (total - flatFee) / (1 + variable)))
        chargeAmountWithFeeIncluded =
            total - Math.min(maxFee, Math.max(minFee, flatFee + variable * total))
    }

    if (transaction_type === 'pay-in') {
        chargeAmountWithFeeAdded =
            total + Math.min(maxFee, Math.max(minFee, flatFee + variable * total));
        chargeAmountWithFeeIncluded =
            Math.max(total + minFee, Math.min(total + maxFee, (total + flatFee) / (1 - variable)));
    }

    return amountFee.is_included
        ? round(chargeAmountWithFeeIncluded, chargeCurrency)
        : round(chargeAmountWithFeeAdded, chargeCurrency);
}
